'use client';
import React, { useState } from 'react';

type BillingCycle = 'monthly' | 'annual';

const starterFeatures = [
  'Catálogo público con tu dominio',
  'Hasta 5 vehículos activos',
  'Botón WhatsApp en cada vehículo',
  'Hasta 3 fotos por vehículo',
  'Ficha técnica PDF pública',
  'Archivo de vendidos básico',
  '1 cuenta de usuario',
];

const starterMissing = [
  'Relleno automático con IA',
  'Pipeline de ventas (Kanban)',
  'CRM de clientes',
  'Dashboard de finanzas',
  'Alertas automáticas',
  'Acceso por roles de equipo',
  'Sitio web completo con dominio propio',
];

const proFeatures = [
  'Relleno automático de especificaciones con IA',
  'Pipeline Kanban completo (5 etapas)',
  'Badges Hot / Warm / Cold por prospecto',
  'Vehículos y fotos ilimitados',
  'CRM completo con perfiles de clientes',
  'Historial de actividad completo',
  'Alertas automáticas (30 y 45 días en lote)',
  'Dashboard de finanzas completo + gráficas',
  'Ficha técnica confidencial (con costo, VIN)',
  'Exportar tarjeta de marketing como PNG',
  'Hasta 10 cuentas de usuario con roles',
  'Marca blanca completa (sin logo DriveX)',
  'Sección de Merch',
  'Subcategorías Can-Am y Sea-Doo',
];

const eliteFeatures = [
  'Sitio web propio con tu dominio personalizado',
  'Inventario en tiempo real (se actualiza automáticamente)',
  'Categorías: Nuevos / Usados / Acuáticos / Off-Road',
  'Buscador con filtros: tipo, marca, modelo, precio',
  'Páginas de detalle por vehículo con galería completa',
  'Botón WhatsApp en cada vehículo del sitio',
  'Ficha técnica PDF descargable desde el sitio',
  'Página de contacto con mapa de Google integrado',
  'Soporte para múltiples ubicaciones',
  'Horarios de atención por sucursal',
  'Sección "Vende tu vehículo" con formulario',
  'Newsletter y captura de emails',
  'Links a redes sociales',
  'SEO automático por vehículo',
  'Responsive en todos los dispositivos',
];

export default function DriveXPricing() {
  const [billing, setBilling] = useState<BillingCycle>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const getPriceId = (plan: 'starter' | 'pro' | 'elite', cycle: BillingCycle): string => {
    const map: Record<string, string> = {
      'starter_monthly': process.env.NEXT_PUBLIC_STRIPE_STARTER_MONTHLY_PRICE_ID ?? '',
      'starter_annual': process.env.NEXT_PUBLIC_STRIPE_STARTER_ANNUAL_PRICE_ID ?? '',
      'pro_monthly': process.env.NEXT_PUBLIC_STRIPE_PRO_MONTHLY_PRICE_ID ?? '',
      'pro_annual': process.env.NEXT_PUBLIC_STRIPE_PRO_ANNUAL_PRICE_ID ?? '',
      'elite_monthly': process.env.NEXT_PUBLIC_STRIPE_ELITE_MONTHLY_PRICE_ID ?? '',
      'elite_annual': process.env.NEXT_PUBLIC_STRIPE_ELITE_ANNUAL_PRICE_ID ?? '',
    };
    return map[`${plan}_${cycle}`] ?? '';
  };

  const handleCheckout = async (plan: 'starter' | 'pro' | 'elite') => {
    setLoadingPlan(plan);
    try {
      const priceId = getPriceId(plan, billing);
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL ?? '';

      if (!priceId) {
        alert('Este plan no está disponible en este momento. Por favor contáctanos.');
        return;
      }

      const res = await fetch(`${supabaseUrl}/functions/v1/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, billing, priceId }),
      });
      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        alert(data?.error || 'Error al procesar el pago. Intenta de nuevo.');
      }
    } catch {
      alert('Error al procesar el pago. Intenta de nuevo.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const gold = '#C9A84C';
  const goldLight = '#E8C96A';

  return (
    <section
      id="pricing"
      style={{ background: '#FFFFFF', padding: '100px 0', fontFamily: 'Montserrat, sans-serif' }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
        {/* Eyebrow + Headline */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontWeight: 700, fontSize: '10px', color: '#C8102E', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: '16px' }}>
            PRECIOS TRANSPARENTES
          </div>
          <h2 style={{ fontWeight: 800, fontSize: 'clamp(28px, 4vw, 46px)', color: '#0A0A0A', marginBottom: '0' }}>
            Sin contratos. Sin sorpresas. Cancela cuando quieras.
          </h2>
        </div>

        {/* Billing toggle */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '48px' }}>
          <button
            onClick={() => setBilling('monthly')}
            style={{ fontWeight: 700, fontSize: '13px', padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', background: billing === 'monthly' ? '#C8102E' : '#E5E5E5', color: billing === 'monthly' ? '#FFFFFF' : '#555555', transition: 'all 0.15s ease', fontFamily: 'Montserrat, sans-serif' }}
          >
            Mensual
          </button>
          <button
            onClick={() => setBilling('annual')}
            style={{ fontWeight: 700, fontSize: '13px', padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', background: billing === 'annual' ? '#C8102E' : '#E5E5E5', color: billing === 'annual' ? '#FFFFFF' : '#555555', transition: 'all 0.15s ease', fontFamily: 'Montserrat, sans-serif', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            Anual
            {billing === 'monthly' && (
              <span style={{ background: '#C8102E', color: '#FFF', fontSize: '9px', fontWeight: 700, padding: '2px 6px', borderRadius: '10px' }}>
                Ahorra 20%
              </span>
            )}
          </button>
        </div>

        {/* Three-column cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px', alignItems: 'start' }}>

          {/* ── STARTER ── */}
          <div style={{ background: '#FFFFFF', border: '0.5px solid #E5E5E5', borderRadius: '12px', padding: '32px' }}>
            <div style={{ fontWeight: 700, fontSize: '10px', color: '#888888', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '6px' }}>STARTER</div>
            <div style={{ fontWeight: 700, fontSize: '22px', color: '#0A0A0A', marginBottom: '8px' }}>Catálogo básico</div>
            <p style={{ fontWeight: 400, fontSize: '13px', color: '#888888', lineHeight: 1.6, fontStyle: 'italic', marginBottom: '20px' }}>
              Un catálogo digital para existir en línea. Suficiente para empezar. No suficiente para crecer.
            </p>
            <div style={{ marginBottom: '20px' }}>
              <span style={{ fontWeight: 900, fontSize: '48px', color: '#0A0A0A' }}>
                {billing === 'monthly' ? '$99' : '$79'}
              </span>
              <span style={{ fontWeight: 400, fontSize: '16px', color: '#888888' }}> / mes</span>
              {billing === 'annual' && (
                <div style={{ fontWeight: 400, fontSize: '12px', color: '#888888', marginTop: '4px' }}>facturado $948/año</div>
              )}
            </div>
            <div style={{ background: '#FFFBEB', border: '1px solid #F59E0B', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ fontSize: '14px', flexShrink: 0 }}>⚠️</span>
              <span style={{ fontWeight: 600, fontSize: '12px', color: '#92400E' }}>
                Límite de 5 vehículos · 1 usuario · Sin pipeline · Sin CRM
              </span>
            </div>
            <div style={{ fontWeight: 700, fontSize: '10px', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>Incluido</div>
            {starterFeatures.map((f) => (
              <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                <span style={{ color: '#10B981', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>✓</span>
                <span style={{ fontWeight: 400, fontSize: '13px', color: '#555555' }}>{f}</span>
              </div>
            ))}
            <div style={{ fontWeight: 700, fontSize: '10px', color: '#888888', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px', marginTop: '16px' }}>No incluido</div>
            {starterMissing.map((f) => (
              <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '5px' }}>
                <span style={{ color: '#CCCCCC', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>—</span>
                <span style={{ fontWeight: 400, fontSize: '13px', color: '#AAAAAA', fontStyle: 'italic' }}>{f}</span>
              </div>
            ))}
            <button
              onClick={() => handleCheckout('starter')}
              disabled={loadingPlan === 'starter'}
              style={{ width: '100%', fontWeight: 700, fontSize: '13px', color: '#0A0A0A', background: '#FFFFFF', border: '1px solid #0A0A0A', borderRadius: '6px', padding: '14px', cursor: loadingPlan === 'starter' ? 'not-allowed' : 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: '24px', transition: 'background 0.2s ease', fontFamily: 'Montserrat, sans-serif', opacity: loadingPlan === 'starter' ? 0.7 : 1 }}
              onMouseEnter={e => { if (!loadingPlan) (e.currentTarget as HTMLElement).style.background = '#F5F5F5'; }}
              onMouseLeave={e => { if (!loadingPlan) (e.currentTarget as HTMLElement).style.background = '#FFFFFF'; }}
            >
              {loadingPlan === 'starter' ? 'Procesando...' : 'EMPEZAR CON STARTER'}
            </button>
          </div>

          {/* ── PRO ── */}
          <div style={{ position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: '-16px', position: 'relative', zIndex: 1 }}>
              <span style={{ fontWeight: 700, fontSize: '11px', color: '#FFFFFF', background: '#C8102E', padding: '6px 16px', borderRadius: '20px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Más popular
              </span>
            </div>
            <div style={{ background: '#FFFFFF', border: '2px solid #C8102E', borderRadius: '12px', padding: '32px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: '#C8102E' }} />
              <div style={{ fontWeight: 700, fontSize: '10px', color: '#C8102E', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '6px', marginTop: '8px' }}>PRO</div>
              <div style={{ fontWeight: 700, fontSize: '22px', color: '#0A0A0A', marginBottom: '8px' }}>DriveX Pro</div>
              <p style={{ fontWeight: 400, fontSize: '13px', color: '#555555', lineHeight: 1.6, marginBottom: '20px' }}>
                El sistema operativo completo para tu concesionario. CRM, pipeline, finanzas y marketing en uno.
              </p>
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontWeight: 900, fontSize: '48px', color: '#C8102E' }}>
                  {billing === 'monthly' ? '$299' : '$249'}
                </span>
                <span style={{ fontWeight: 400, fontSize: '16px', color: '#888888' }}> / mes</span>
                {billing === 'annual' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ fontWeight: 400, fontSize: '12px', color: '#888888' }}>facturado $2,988/año</span>
                    <span style={{ fontWeight: 700, fontSize: '10px', color: '#10B981', background: '#D1FAE5', padding: '2px 6px', borderRadius: '4px' }}>Ahorras $600</span>
                  </div>
                )}
              </div>
              <div style={{ background: '#FDECEA', border: '0.5px solid #C8102E', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '10px', color: '#C8102E', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Lo que pagarías por separado:</div>
                {[['CRM + Pipeline + Finanzas', '~$150/mes'],['Catálogo + sitio básico', '$79/mes'],['Herramienta de PDF + IA', '$69/mes']].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 400, fontSize: '12px', color: '#555555', lineHeight: 1.8 }}>
                    <span>{k}</span><span>{v}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid rgba(200,16,46,0.2)', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '12px', color: '#C8102E' }}>
                  <span>DriveX Pro:</span><span>$299/mes — todo incluido</span>
                </div>
              </div>
              <div style={{ fontWeight: 600, fontSize: '12px', color: '#0A0A0A', marginBottom: '10px' }}>Todo lo del plan Starter, más:</div>
              {proFeatures.map((f) => (
                <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ color: '#C8102E', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>✓</span>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#0A0A0A' }}>{f}</span>
                </div>
              ))}
              <div style={{ textAlign: 'center', margin: '20px 0 4px' }}>
                <span style={{ fontWeight: 600, fontSize: '12px', color: '#C8102E', background: 'rgba(200,16,46,0.08)', padding: '4px 12px', borderRadius: '20px' }}>
                  14 días gratis — sin tarjeta de crédito
                </span>
              </div>
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <span style={{ fontWeight: 400, fontSize: '11px', color: '#888888' }}>
                  ¿Quieres también un sitio web completo?{' '}
                  <a href="#elite-card" style={{ color: '#C9A84C', textDecoration: 'none', fontWeight: 600 }}>Mira el plan Elite →</a>
                </span>
              </div>
              <button
                onClick={() => handleCheckout('pro')}
                disabled={loadingPlan === 'pro'}
                style={{ width: '100%', fontWeight: 700, fontSize: '14px', color: '#FFFFFF', background: '#C8102E', border: 'none', borderRadius: '6px', padding: '16px', cursor: loadingPlan === 'pro' ? 'not-allowed' : 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'background 0.2s ease', fontFamily: 'Montserrat, sans-serif', opacity: loadingPlan === 'pro' ? 0.7 : 1 }}
                onMouseEnter={e => { if (!loadingPlan) (e.currentTarget as HTMLElement).style.background = '#9B0D22'; }}
                onMouseLeave={e => { if (!loadingPlan) (e.currentTarget as HTMLElement).style.background = '#C8102E'; }}
                onMouseDown={e => ((e.currentTarget as HTMLElement).style.transform = 'scale(0.99)')}
                onMouseUp={e => ((e.currentTarget as HTMLElement).style.transform = 'scale(1)')}
              >
                {loadingPlan === 'pro' ? 'PROCESANDO...' : 'EMPEZAR PRUEBA GRATIS'}
              </button>
            </div>
          </div>

          {/* ── ELITE ── */}
          <div id="elite-card" style={{ position: 'relative' }}>
            <div style={{ textAlign: 'center', marginBottom: '-16px', position: 'relative', zIndex: 1 }}>
              <span style={{ fontWeight: 800, fontSize: '11px', color: '#0A0A0A', background: gold, padding: '6px 16px', borderRadius: '20px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Todo incluido — el más completo
              </span>
            </div>
            <div style={{ background: '#0A0A0A', border: `2px solid ${gold}`, borderRadius: '12px', padding: '32px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: gold }} />
              <div style={{ fontWeight: 700, fontSize: '10px', color: gold, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '6px', marginTop: '8px' }}>ELITE</div>
              <div style={{ fontWeight: 700, fontSize: '22px', color: '#FFFFFF', marginBottom: '8px' }}>DriveX Elite</div>
              <p style={{ fontWeight: 400, fontSize: '13px', color: '#AAAAAA', lineHeight: 1.6, marginBottom: '20px' }}>
                Tu concesionario completo en línea. Sitio web propio, inventario en tiempo real, sin desarrolladores ni actualizaciones manuales.
              </p>
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontWeight: 900, fontSize: '48px', color: gold }}>
                  {billing === 'monthly' ? '$599' : '$499'}
                </span>
                <span style={{ fontWeight: 400, fontSize: '16px', color: '#888888' }}> / mes</span>
                {billing === 'annual' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <span style={{ fontWeight: 400, fontSize: '12px', color: '#888888' }}>facturado $5,988/año</span>
                    <span style={{ fontWeight: 700, fontSize: '10px', color: '#0A0A0A', background: gold, padding: '2px 6px', borderRadius: '4px' }}>Ahorras $1,200</span>
                  </div>
                )}
              </div>

              {/* Value comparison box */}
              <div style={{ background: 'rgba(201,168,76,0.1)', border: `0.5px solid ${gold}`, borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '10px', color: gold, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>
                  🌐 Lo que pagarías normalmente por esto:
                </div>
                {[
                  ['Todo el plan Pro (CRM, pipeline, finanzas)', '$299/mes'],
                  ['Diseño web personalizado (única vez)', '$3,000–$8,000'],
                  ['Hosting + mantenimiento', '$100–$200/mes'],
                  ['Actualizaciones manuales de inventario', '10–15 hrs/mes'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 400, fontSize: '12px', color: '#CCCCCC', lineHeight: 1.8 }}>
                    <span>{k}</span><span style={{ color: '#888888', flexShrink: 0, marginLeft: '8px' }}>{v}</span>
                  </div>
                ))}
                <div style={{ borderTop: `1px solid rgba(201,168,76,0.3)`, marginTop: '8px', paddingTop: '8px', fontWeight: 700, fontSize: '12px', color: gold }}>
                  DriveX Elite: $599/mes — todo incluido, cero trabajo manual
                </div>
                <div style={{ fontWeight: 600, fontSize: '11px', color: gold, marginTop: '6px' }}>
                  El sitio se actualiza solo. Cada vez que agregas o vendes un vehículo.
                </div>
              </div>

              <div style={{ fontWeight: 600, fontSize: '12px', color: '#FFFFFF', marginBottom: '10px' }}>Todo lo del plan Pro, más:</div>
              {eliteFeatures.map((f) => (
                <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ color: gold, fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>✓</span>
                  <span style={{ fontWeight: 500, fontSize: '13px', color: '#FFFFFF' }}>{f}</span>
                </div>
              ))}

              <div style={{ textAlign: 'center', margin: '20px 0 16px' }}>
                <span style={{ fontWeight: 600, fontSize: '12px', color: '#0A0A0A', background: gold, padding: '4px 12px', borderRadius: '20px' }}>
                  14 días gratis — sin tarjeta de crédito
                </span>
              </div>
              <button
                onClick={() => handleCheckout('elite')}
                disabled={loadingPlan === 'elite'}
                style={{ width: '100%', fontWeight: 800, fontSize: '14px', color: '#0A0A0A', background: gold, border: 'none', borderRadius: '6px', padding: '16px', cursor: loadingPlan === 'elite' ? 'not-allowed' : 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'background 0.2s ease', fontFamily: 'Montserrat, sans-serif', opacity: loadingPlan === 'elite' ? 0.7 : 1 }}
                onMouseEnter={e => { if (!loadingPlan) (e.currentTarget as HTMLElement).style.background = goldLight; }}
                onMouseLeave={e => { if (!loadingPlan) (e.currentTarget as HTMLElement).style.background = gold; }}
                onMouseDown={e => ((e.currentTarget as HTMLElement).style.transform = 'scale(0.99)')}
                onMouseUp={e => ((e.currentTarget as HTMLElement).style.transform = 'scale(1)')}
              >
                {loadingPlan === 'elite' ? 'PROCESANDO...' : 'EMPEZAR PRUEBA GRATIS'}
              </button>
            </div>
          </div>
        </div>

        {/* Enterprise */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <span style={{ fontWeight: 400, fontSize: '13px', color: '#888888' }}>
            ¿Tienes una cadena de concesionarios o más de 10 usuarios?{' '}
          </span>
          <a
            href="mailto:enterprise@btsaas.com"
            style={{ fontWeight: 600, fontSize: '13px', color: '#C8102E', textDecoration: 'none' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.textDecoration = 'underline')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.textDecoration = 'none')}
          >
            Escríbenos para un plan Enterprise →
          </a>
        </div>
      </div>
    </section>
  );
}
